// Copyright 2025, Imprivata, Inc.  All rights reserved.

import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  credentialDelete,
  credentialsFind,
  findAuthenticatorsByUserIds,
  unenrollAuthenticators,
} from '../../../../api/portalServices';

export const useUserAuthenticators = (userId: string) => {
  return useQuery({
    queryKey: [QueryKey.AUTHENTICATORS, userId],
    queryFn: async () => {
      const response = await findAuthenticatorsByUserIds({ userIds: [userId] });
      return response.authenticators;
    },
    retry: false,
    staleTime: 0,
  });
};

export function useUserCredentials(userId: string) {
  return useQuery({
    queryKey: [QueryKey.CREDENTIALS, userId],
    queryFn: async () => {
      const response = await credentialsFind({ userId: userId });
      return response.credentialInfo;
    },
    retry: false,
    staleTime: 0,
  });
}

export const landingPageDataTanstackCacheKey = 'user-self-service-landing-page-data';

export const useUnenrollAuthenticator = (
  // I guess USS needs this since there is no tenant id in the URL
  tenantId: string,
  options: { onSettled?: () => void; onError?: (e: ApiError) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (authnId: string) => {
      await unenrollAuthenticators({ authenticatorIds: [authnId] }, tenantId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKey.AUTHENTICATORS] });
    },
    retry: false,
    onSettled: options?.onSettled,
    onError: options?.onError,
  });
};

export function useCredentialDelete(options: { onError?: (e: ApiError) => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentialId: string) => {
      await credentialDelete({ credentialId });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKey.CREDENTIALS] });
    },
    retry: false,
    onError: options?.onError,
  });
}
